// src/pages/SearchOperators.tsx
import React, { useEffect } from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from '@heroicons/react/20/solid';

export default function SearchOperators() {
  useEffect(() => {
    document.title = "Search Operator Guide • BacklinkBanana";
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold text-blue-700 mb-6">
        How to Use Google Search Operators to Find Backlink Opportunities
      </h1>

      <p className="mb-8 text-gray-700">
        This guide teaches you how to combine powerful Google search operators with Backlink Banana to uncover backlink-worthy sites quickly and efficiently.
      </p>

      {[
        {
          title: "What Are Google Search Operators?",
          content: (
            <p className="text-gray-700">
              Search operators are special commands you can use in Google Search to filter, refine, and enhance your results. Instead of manually browsing irrelevant links, operators help you zero in on backlink prospects like guest post pages, resource lists, or niche directories.
            </p>
          ),
        },
        {
          title: "Most Useful Operators",
          content: (
            <div className="overflow-x-auto">
              <table className="table-auto border border-gray-300 w-full text-sm text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border px-4 py-2 font-medium">Operator</th>
                    <th className="border px-4 py-2 font-medium">Function</th>
                    <th className="border px-4 py-2 font-medium">Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border px-4 py-2">site:</td><td className="border px-4 py-2">Limit results to one domain</td><td className="border px-4 py-2">site:forbes.com</td></tr>
                  <tr><td className="border px-4 py-2">intitle:</td><td className="border px-4 py-2">Search titles only</td><td className="border px-4 py-2">intitle:"guest post"</td></tr>
                  <tr><td className="border px-4 py-2">inurl:</td><td className="border px-4 py-2">Search inside URLs</td><td className="border px-4 py-2">inurl:write-for-us</td></tr>
                  <tr><td className="border px-4 py-2">"quoted phrase"</td><td className="border px-4 py-2">Exact match</td><td className="border px-4 py-2">"backlink strategy"</td></tr>
                  <tr><td className="border px-4 py-2">-</td><td className="border px-4 py-2">Exclude terms</td><td className="border px-4 py-2">SEO -agency</td></tr>
                  <tr><td className="border px-4 py-2">OR</td><td className="border px-4 py-2">Either of two terms</td><td className="border px-4 py-2">"submit guest post" OR "write for us"</td></tr>
                </tbody>
              </table>
            </div>
          ),
        },
        {
          title: "Advanced Search Operator Techniques",
          content: (
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li><strong>filetype:</strong> Find PDFs or PPTs. E.g., <code>filetype:pdf SEO</code></li>
              <li><strong>related:</strong> Find similar sites. E.g., <code>related:moz.com</code></li>
              <li><strong>* (wildcard):</strong> Fuzzy match. E.g., <code>"top * SEO tools"</code></li>
              <li><strong>intext:</strong> Match words in body content. E.g., <code>intext:"guest contributor"</code></li>
              <li><strong>Parentheses + logic:</strong> Combine multiple searches. E.g., <code>("guest post" OR "write for us") AND SaaS</code></li>
              <li><strong>Date filtering:</strong> Find recent content using Google tools or <code>after:2023</code></li>
            </ul>
          ),
        },
        {
          title: "Real-World Examples by Industry",
          content: (
            <div className="space-y-4 text-gray-700">
              <div>
                <strong className="text-blue-700">SaaS SEO Outreach:</strong>
                <p><code>intitle:"write for us" AND ("SaaS" OR "software") AND "SEO agency"</code></p>
              </div>
              <div>
                <strong className="text-blue-700">Blog Link Exchanges:</strong>
                <p><code>intext:"link exchange" AND ("digital marketing" OR "SEO") site:blogspot.com</code></p>
              </div>
              <div>
                <strong className="text-blue-700">Local Business Listings:</strong>
                <p><code>"submit your business" AND ("India" OR ".in") filetype:pdf</code></p>
              </div>
              <div>
                <strong className="text-blue-700">E-commerce Roundups:</strong>
                <p><code>intitle:"top * ecommerce tools"</code></p>
              </div>
              <div>
                <strong className="text-blue-700">Podcast Features:</strong>
                <p><code>intitle:"SEO podcast" AND ("guest episode" OR "interview with")</code></p>
              </div>
              <div>
                <strong className="text-blue-700">.edu Mentions:</strong>
                <p><code>site:.edu intitle:"recommended SEO tools"</code></p>
              </div>
            </div>
          ),
        },
        {
          title: "Using Backlink Banana with Operators",
          content: (
            <>
              <p className="mb-2 text-gray-700">
                Backlink Banana is built to fully support Google search operators. Just type in a custom query, and we’ll retrieve live results with DA, PA, and Spam Score.
              </p>
              <ul className="list-disc ml-6 text-gray-700">
                <li><code>"submit guest post" site:.edu</code></li>
                <li><code>intitle:"top SEO blogs" -2020</code></li>
                <li><code>inurl:links "digital marketing"</code></li>
              </ul>
            </>
          ),
        },
        {
          title: "Tips for Better Results",
          content: (
            <ul className="list-disc ml-6 text-gray-700 space-y-2">
              <li>Use quotes for exact phrases</li>
              <li>Combine operators for highly specific searches</li>
              <li>Use <code>-</code> to exclude low-quality or irrelevant results</li>
              <li>Use time filters in Google to find fresh content</li>
              <li>Always check DA, PA, and Spam Score before outreach</li>
            </ul>
          ),
        },
      ].map((item, index) => (
        <Disclosure key={index} defaultOpen={index === 0}>
          {({ open }) => (
            <div className="border-b py-4">
              <Disclosure.Button className="flex justify-between w-full text-left text-lg font-semibold text-blue-800 focus:outline-none">
                <span>{item.title}</span>
                <ChevronUpIcon
                  className={`w-5 h-5 text-blue-600 transform transition-transform ${open ? 'rotate-180' : 'rotate-0'}`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="mt-2">{item.content}</Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      ))}

      {/* Footer */}
      <footer className="mt-8 text-gray-500 text-xs text-center px-4">
        <div className="space-x-4 mb-2">
          <a href="/privacy-policy" className="hover:underline">
            Privacy Policy
          </a>
          <span>|</span>
          <a href="/terms-and-conditions" className="hover:underline">
            Terms & Conditions
          </a>
        </div>
        &copy; {new Date().getFullYear()} Backlink Banana
      </footer>
    </div>
  );
}
